import React, { useState, useRef, useEffect } from 'react';
import './MonthYearPicker.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthYearPicker = ({
  value,
  onChange,
  label = 'Select Date',
  placeholder = 'Select month and year',
  minYear = 1960,
  maxDate = new Date(),
  required = false,
  error = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const containerRef = useRef(null);
  const yearListRef = useRef(null);

  const currentYear = maxDate.getFullYear();
  const currentMonth = maxDate.getMonth();

  // Generate years array from minYear to current year
  const years = [];
  for (let y = currentYear; y >= minYear; y--) {
    years.push(y);
  }

  // Parse initial value or clear when value is reset
  useEffect(() => {
    if (value) {
      const [year, month] = value.split('-').map(Number);
      if (year && month) {
        setSelectedYear(year);
        setSelectedMonth(month - 1); // Convert to 0-indexed
      }
    } else {
      // Clear selections when value is reset
      setSelectedYear(null);
      setSelectedMonth(null);
    }
  }, [value]);

  // Scroll to selected year when dropdown opens
  useEffect(() => {
    if (isOpen && yearListRef.current && selectedYear) {
      const selectedElement = yearListRef.current.querySelector('.year-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }
  }, [isOpen, selectedYear]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);

    // If year is selected, emit the value
    if (selectedYear !== null) {
      emitValue(monthIndex, selectedYear);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);

    // If selecting a year that would make current month invalid, reset month
    if (year === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
      emitValue(currentMonth, year);
    } else if (selectedMonth !== null) {
      emitValue(selectedMonth, year);
    }
  };

  const emitValue = (month, year) => {
    const formattedMonth = String(month + 1).padStart(2, '0');
    const newValue = `${year}-${formattedMonth}`;
    onChange(newValue);
    setIsOpen(false);
  };

  const isMonthDisabled = (monthIndex) => {
    if (selectedYear === currentYear) {
      return monthIndex > currentMonth;
    }
    return false;
  };

  const formatDisplayValue = () => {
    if (selectedMonth !== null && selectedYear !== null) {
      return `${MONTHS[selectedMonth]} ${selectedYear}`;
    }
    return '';
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedMonth(null);
    setSelectedYear(null);
    onChange('');
  };

  return (
    <div className="month-year-picker" ref={containerRef}>
      {label && (
        <label className="picker-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      <div
        className={`picker-input ${isOpen ? 'open' : ''} ${error ? 'has-error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="picker-value">
          {formatDisplayValue() || <span className="placeholder">{placeholder}</span>}
        </div>
        <div className="picker-icons">
          {(selectedMonth !== null || selectedYear !== null) && (
            <button
              type="button"
              className="clear-btn"
              onClick={handleClear}
              title="Clear"
            >
              <i className="bi bi-x"></i>
            </button>
          )}
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
        </div>
      </div>

      {error && <div className="picker-error">{error}</div>}

      {isOpen && (
        <div className="picker-dropdown">
          <div className="picker-panels">
            {/* Months Panel */}
            <div className="months-panel">
              <div className="panel-header">Month</div>
              <div className="months-grid">
                {MONTHS.map((month, index) => (
                  <button
                    key={month}
                    type="button"
                    className={`month-item ${selectedMonth === index ? 'selected' : ''} ${isMonthDisabled(index) ? 'disabled' : ''}`}
                    onClick={() => !isMonthDisabled(index) && handleMonthSelect(index)}
                    disabled={isMonthDisabled(index)}
                  >
                    {month.substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            {/* Years Panel */}
            <div className="years-panel">
              <div className="panel-header">Year</div>
              <div className="years-list" ref={yearListRef}>
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={`year-item ${selectedYear === year ? 'selected' : ''}`}
                    onClick={() => handleYearSelect(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
